import * as THREE from "three";
import { InterpolationSink } from "../../../physics/physics_interpolation.js";
import RAPIER from "@dimforge/rapier3d-compat";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { ResourceManager } from "../../../resource_loader/resource_manager.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { SceneManager } from "../../../core/scene_manager.js";
import { randomRange } from "../../../common/common_funcs.js";
import { IceBlock } from "../../../entities/ice_block.js";
import { PhysicsWorld } from "../../../physics/physics_world.js";
import { PhysicsFriction, PhysicsRestitution, PhysicsStack } from "../../../physics/physics_masses.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { ShadowBatcher } from "../../../core/shadow_batcher.js";

type FragmentState = {
  mesh: THREE.Mesh;
  initial_pos: THREE.Vector3;
  initial_quat: THREE.Quaternion;
  vx: number;
  vy: number;
  vz: number;
  rot_axis: THREE.Vector3;
  rot_speed: number;
  friction: number;
};

// A fragment "promoted" to a real Rapier rigid body so it can physically
// collide with the rest of the level instead of just following the
// decorative kinematic formula.
type PromotedFragment = {
  mesh: THREE.Mesh;
  material: THREE.MeshLambertMaterial;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  age: number;
  fading: boolean;
  fade_timer: number;
};

export class VfxBlockShatterTask implements IVfxTask, IController
{
  private static readonly DURATION: number = 0.65;
  private static readonly DEFAULT_SCALE: number = 0.3;
  private static readonly DEFAULT_MASS: number = 6.0;
  private static readonly BASE_MASS: number = 6.0;

  private static readonly SPEED_MIN: number = 1;
  private static readonly SPEED_MAX: number = 2;

  private static readonly VY_MIN: number = 0.0;
  private static readonly VY_MAX: number = 0.25;

  private static readonly FRICTION_MIN: number = 1.4;
  private static readonly FRICTION_MAX: number = 2.2;

  private static readonly ROT_SPEED_MIN: number = 7.0;
  private static readonly ROT_SPEED_MAX: number = 14.0;

  private static readonly GRAVITY: number = 9.8;
  private static readonly GROUND_Y_FACTOR: number = 0.1;

  private static readonly BASE_IMPACT_SPEED: number = 6;
  private static readonly IMPACT_SPEED_FACTOR: number = 0.24;
  private static readonly MAX_IMPACT_MULTIPLIER: number = 3.8;

  private static readonly ROUGHNESS: number = 0.24;
  private static readonly METALNESS: number = 0.0;
  private static readonly ANISOTROPY: number = 8;

  // How many fragments become "real" physics debris that can knock into
  // neighboring blocks, instead of just flying through them decoratively.
  private static readonly PROMOTED_FRAGMENTS: number = 3;
  private static readonly FOUR_PROMOTED_CHANCE: number = 0.25;
  private static readonly BOOSTER_MIN_PROMOTED: number = 2;
  private static readonly BOOSTER_MAX_PROMOTED: number = 3;

  private static readonly MIN_DEBRIS_MASS: number = 0.5;
  private static readonly DEBRIS_MIN_HALF_SIZE: number = 0.02;
  private static readonly DEBRIS_LINEAR_DAMPING: number = 0.3;
  private static readonly DEBRIS_ANGULAR_DAMPING: number = 0.5;
  private static readonly DEBRIS_MAX_LINEAR_SPEED: number = 6;
  private static readonly DEBRIS_MAX_ANGULAR_SPEED: number = 15;
  private static readonly DEBRIS_MIN_SETTLE_TIME: number = 0.4;
  private static readonly DEBRIS_REST_LINVEL_SQ_THRESHOLD: number = 0.02;
  private static readonly DEBRIS_MAX_LIFETIME: number = 6.0;
  private static readonly DEBRIS_FALL_MARGIN: number = 1.0;
  private static readonly DEBRIS_FADE_DURATION: number = 0.5;

  public is_active = true;

  // Live debris drives physics bodies of its own, so the shadow map must keep
  // refreshing while any shatter is running.
  private static live_tasks = 0;
  public static hasLiveDebris(): boolean { return this.live_tasks > 0; }

  private static readonly shared_maps = new Map<string, THREE.Texture>();

  public static mapsCreated(): number { return this.shared_maps.size; }

  private static readonly owned_geometries = new WeakSet<THREE.BufferGeometry>();

  private static readonly live = new Set<VfxBlockShatterTask>();

  static
  {
    ShadowBatcher.setSource( add =>
    {
      for ( const task of VfxBlockShatterTask.live )
        for ( const p of task.promoted )
          add( p.mesh );
    } );
  }

  public static collectInterpolated( add: InterpolationSink ): void
  {
    for ( const task of this.live )
      for ( const p of task.promoted )
        add( p.mesh, false );
  }

  public static createDebrisMaterial( map?: THREE.Texture, is_ice = false ): THREE.MeshLambertMaterial
  {
    return new THREE.MeshLambertMaterial( {
      map,
      color: 0xffffff,
      emissive: is_ice ? IceBlock.SHADOW_FILL_COLOR : 0x000000,
      emissiveMap: is_ice ? map : null,
      emissiveIntensity: is_ice ? IceBlock.SHADOW_FILL_INTENSITY : 1,
      transparent: true,
      opacity: 1.0,
      side: THREE.FrontSide,
      depthWrite: false,
    } );
  }

  public static prewarmMaps( shatters: Iterable<{ texture_id: ResourceID; blocks: number }> ): THREE.Texture[]
  {
    const maps: THREE.Texture[] = [];
    for ( const { texture_id, blocks } of shatters )
    {
      const map = this.sharedMap( texture_id, Math.round( blocks ) );
      if ( map )
        maps.push( map );
    }
    return maps;
  }

  private static sharedMap( texture_id: ResourceID, blocks: number ): THREE.Texture | null
  {
    const map_key = `${ texture_id }|${ blocks }`;
    const cached = this.shared_maps.get( map_key );
    if ( cached )
      return cached;

    const source_map = ResourceManager.getTexture( texture_id );
    if ( !source_map )
      return null;

    const map = source_map.clone();
    map.flipY = false;
    IceBlock.applyCrackedTextureUV( map, blocks, false );
    map.minFilter = THREE.LinearMipmapLinearFilter;
    map.magFilter = THREE.LinearFilter;
    map.generateMipmaps = true;
    map.anisotropy = VfxBlockShatterTask.ANISOTROPY;
    map.needsUpdate = true;
    this.shared_maps.set( map_key, map );
    return map;
  }

  public static createIceWarmupProbe(): THREE.Mesh
  {
    return this.createWarmupProbe( ResourceID.IceCrackedTexture, true );
  }

  public static createWarmupProbe( texture_id = ResourceID.GreenBoxTexture, is_ice = false ): THREE.Mesh
  {
    const material = this.createDebrisMaterial(
      ResourceManager.getTexture( texture_id ) ?? undefined,
      is_ice
    );

    const mesh = new THREE.Mesh( new THREE.BoxGeometry( 0.01, 0.01, 0.01 ), material );
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.renderOrder = 10;

    return mesh;
  }

  private root: THREE.Object3D;
  private material: THREE.MeshLambertMaterial;
  private fragments: FragmentState[] = [];
  private promoted: PromotedFragment[] = [];
  private decorative_disposed = false;
  private total_mass = 0;
  private last_now?: number;

  private start_time: number;
  private duration: number = VfxBlockShatterTask.DURATION;
  private floor_y: number;

  constructor(
    position: THREE.Vector3,
    rotation: THREE.Quaternion = new THREE.Quaternion(),
    scale: number | THREE.Vector3 = VfxBlockShatterTask.DEFAULT_SCALE,
    texture_id: ResourceID = ResourceID.GreenBoxTexture,
    mass: number = VfxBlockShatterTask.DEFAULT_MASS,
    floor_y: number = 0.0,
    impact_speed: number = 0.0,
    grid_size: THREE.Vector2 = new THREE.Vector2( 1, 1 ),
  )
  {
    this.start_time = Vfx3DRuntime.current_time;
    VfxBlockShatterTask.live_tasks++;
    VfxBlockShatterTask.live.add( this );
    this.floor_y = floor_y;
    this.total_mass = mass;
    this.root = ResourceManager.getModel( ResourceID.BlockDestroyModel );

    this.root.position.copy( position );
    this.root.quaternion.set( 0, 0, 0, 1 ); // Unrotated root aligned with World Space

    const uniform_scale = typeof scale === "number" ? scale : VfxBlockShatterTask.DEFAULT_SCALE;
    this.root.scale.setScalar( uniform_scale );

    const blocks = Math.max( grid_size.x, grid_size.y );
    const is_horizontal = grid_size.x > grid_size.y;

    const map = VfxBlockShatterTask.sharedMap( texture_id, Math.round( blocks ) );

    this.material = VfxBlockShatterTask.createDebrisMaterial(
      map ?? undefined,
      texture_id === ResourceID.IceCrackedTexture
    );

    const stretch_x = is_horizontal ? grid_size.y : grid_size.x;
    const stretch_y = is_horizontal ? grid_size.x : grid_size.y;

    const width_factor = 1 + ( stretch_x - 1 ) * 1.05;
    const height_factor = 1 + ( stretch_y - 1 ) * 1.05;
    const volume_factor = width_factor * height_factor;

    const base_rotation = rotation.clone();
    if ( is_horizontal )
    {
      base_rotation.multiply( new THREE.Quaternion().setFromAxisAngle( new THREE.Vector3( 0, 0, 1 ), -Math.PI * 0.5 ) );
    }

    // Larger/longer fragments have higher volume and physical mass
    const effective_fragment_mass = mass * volume_factor;
    const speed_scale = Math.sqrt( VfxBlockShatterTask.BASE_MASS / Math.max( 1, effective_fragment_mass ) );
    const friction_scale = Math.sqrt( volume_factor );

    const impact_mult = THREE.MathUtils.clamp(
      1.0 + Math.max( 0, impact_speed - VfxBlockShatterTask.BASE_IMPACT_SPEED ) * VfxBlockShatterTask.IMPACT_SPEED_FACTOR,
      1.0,
      VfxBlockShatterTask.MAX_IMPACT_MULTIPLIER
    );

    this.root.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
      {
        child.castShadow = false;
        child.receiveShadow = true;
        child.material = this.material;

        if ( grid_size.x > 1 || grid_size.y > 1 )
        {
          child.geometry = child.geometry.clone();
          VfxBlockShatterTask.owned_geometries.add( child.geometry );
          const pos_attr = child.geometry.attributes.position;
          for ( let i = 0; i < pos_attr.count; i++ )
          {
            pos_attr.setX( i, pos_attr.getX( i ) * width_factor );
            pos_attr.setY( i, pos_attr.getY( i ) * height_factor );
          }
          pos_attr.needsUpdate = true;
        }

        const local_pos = new THREE.Vector3(
          child.position.x * width_factor,
          child.position.y * height_factor,
          child.position.z
        );

        const initial_pos = local_pos.applyQuaternion( base_rotation );
        const initial_quat = base_rotation.clone().multiply( child.quaternion );

        child.position.copy( initial_pos );
        child.quaternion.copy( initial_quat );

        const dir = new THREE.Vector3( initial_pos.x, 0, initial_pos.z );
        if ( dir.lengthSq() < 0.0001 )
        {
          dir.set( randomRange( -1, 1 ), 0, randomRange( -1, 1 ) );
        }
        dir.normalize();

        const speed = randomRange( VfxBlockShatterTask.SPEED_MIN, VfxBlockShatterTask.SPEED_MAX ) * speed_scale * impact_mult;
        const vx = dir.x * speed;
        const vz = dir.z * speed;
        const vy = randomRange( VfxBlockShatterTask.VY_MIN, VfxBlockShatterTask.VY_MAX ) * impact_mult;

        const friction = randomRange( VfxBlockShatterTask.FRICTION_MIN, VfxBlockShatterTask.FRICTION_MAX ) * friction_scale;

        const rot_axis = new THREE.Vector3( dir.z, 0, -dir.x ).normalize();
        const rot_speed = randomRange( VfxBlockShatterTask.ROT_SPEED_MIN, VfxBlockShatterTask.ROT_SPEED_MAX ) * speed_scale * impact_mult;

        this.fragments.push( {
          mesh: child,
          initial_pos,
          initial_quat,
          vx,
          vy,
          vz,
          rot_axis,
          rot_speed,
          friction
        } );
      }
    } );

    SceneManager.world.add( this.root );
    this.promoteRandomFragments();
  }

  public static promoted_override: number | null = null;

  private static booster_break = false;

  public static breakingWithBooster( fn: () => void ): void
  {
    const outer = VfxBlockShatterTask.booster_break;
    VfxBlockShatterTask.booster_break = true;
    try { fn(); }
    finally { VfxBlockShatterTask.booster_break = outer; }
  }

  private static promotedCount(): number
  {
    if ( VfxBlockShatterTask.booster_break )
      return THREE.MathUtils.randInt( VfxBlockShatterTask.BOOSTER_MIN_PROMOTED, VfxBlockShatterTask.BOOSTER_MAX_PROMOTED );
    return Math.random() < VfxBlockShatterTask.FOUR_PROMOTED_CHANCE ? VfxBlockShatterTask.PROMOTED_FRAGMENTS + 1 : VfxBlockShatterTask.PROMOTED_FRAGMENTS;
  }

  private promoteRandomFragments(): void
  {
    if ( this.fragments.length === 0 ) return;

    const wanted = VfxBlockShatterTask.promoted_override ?? VfxBlockShatterTask.promotedCount();

    if ( wanted <= 0 ) return;

    const count = Math.min( this.fragments.length, wanted );

    const pool = [ ...this.fragments ];
    const chosen: FragmentState[] = [];
    for ( let i = 0; i < count && pool.length > 0; i++ )
      chosen.push( pool.splice( Math.floor( Math.random() * pool.length ), 1 )[0] );

    this.fragments = this.fragments.filter( f => !chosen.includes( f ) );

    const per_fragment_mass = Math.max(
      VfxBlockShatterTask.MIN_DEBRIS_MASS,
      this.total_mass / Math.max( 1, chosen.length )
    );

    for ( const frag of chosen ) this.promoteFragment( frag, per_fragment_mass );
  }

  private promoteFragment( frag: FragmentState, mass: number ): void
  {
    const mesh = frag.mesh;

    const world_pos = new THREE.Vector3();
    const world_quat = new THREE.Quaternion();
    const world_scale = new THREE.Vector3();
    mesh.getWorldPosition( world_pos );
    mesh.getWorldQuaternion( world_quat );
    mesh.getWorldScale( world_scale );

    const geometry = mesh.geometry;
    if ( !geometry.boundingBox ) geometry.computeBoundingBox();
    const local_block = geometry.boundingBox!;
    const local_size = local_block.getSize( new THREE.Vector3() );
    const local_center = local_block.getCenter( new THREE.Vector3() );

    const half_x = Math.max( VfxBlockShatterTask.DEBRIS_MIN_HALF_SIZE, local_size.x * world_scale.x * 0.5 );
    const half_y = Math.max( VfxBlockShatterTask.DEBRIS_MIN_HALF_SIZE, local_size.y * world_scale.y * 0.5 );
    const half_z = Math.max( VfxBlockShatterTask.DEBRIS_MIN_HALF_SIZE, local_size.z * world_scale.z * 0.5 );
    const offset = new THREE.Vector3(
      local_center.x * world_scale.x,
      local_center.y * world_scale.y,
      local_center.z * world_scale.z
    );

    // Detach from the decorative root and re-parent directly into the world,
    // baking in the world transform (incl. the root's uniform scale, which no
    // longer cascades down once detached) so there is no visual jump.
    mesh.removeFromParent();
    mesh.position.copy( world_pos );
    mesh.quaternion.copy( world_quat );
    mesh.scale.copy( world_scale );
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.renderOrder = 10;
    SceneManager.world.add( mesh );

    // Own material: the shared decorative material fades out and gets disposed
    // at the end of DURATION, which must not affect a fragment that is going to
    // keep existing (and rendering) long after that. Textures are shared and
    // never disposed, so only the material itself needs cloning.
    const own_material = this.material.clone();
    own_material.opacity = 1.0;
    own_material.transparent = true;
    own_material.depthWrite = true;
    own_material.needsUpdate = true;
    mesh.material = own_material;

    const body = PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation( world_pos.x, world_pos.y, world_pos.z )
        .setRotation( { x: world_quat.x, y: world_quat.y, z: world_quat.z, w: world_quat.w } )
        .setLinearDamping( VfxBlockShatterTask.DEBRIS_LINEAR_DAMPING )
        .setAngularDamping( VfxBlockShatterTask.DEBRIS_ANGULAR_DAMPING )
    );
    body.enableCcd( true );
    body.setAdditionalSolverIterations( PhysicsStack.additional_solver_iterations );

    // Reuse the same explosion velocity/spin that already drives the
    // decorative kinematic formula — just hand it to Rapier instead of a
    // hand-rolled position formula, clamped so an extreme impact-speed
    // multiplier can't fling real debris unrealistically far/fast.
    const linvel = new THREE.Vector3( frag.vx, frag.vy, frag.vz );
    if ( linvel.length() > VfxBlockShatterTask.DEBRIS_MAX_LINEAR_SPEED )
      linvel.setLength( VfxBlockShatterTask.DEBRIS_MAX_LINEAR_SPEED );
    body.setLinvel( { x: linvel.x, y: linvel.y, z: linvel.z }, true );

    const angvel = frag.rot_axis.clone().multiplyScalar( frag.rot_speed );
    if ( angvel.length() > VfxBlockShatterTask.DEBRIS_MAX_ANGULAR_SPEED )
      angvel.setLength( VfxBlockShatterTask.DEBRIS_MAX_ANGULAR_SPEED );
    body.setAngvel( { x: angvel.x, y: angvel.y, z: angvel.z }, true );

    const collider = PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid( half_x, half_y, half_z )
        .setTranslation( offset.x, offset.y, offset.z )
        .setFriction( PhysicsFriction.debris )
        .setRestitution( PhysicsRestitution.debris ),
      body
    );
    collider.setMass( mass );

    this.promoted.push( {
      mesh,
      material: own_material,
      body,
      collider,
      age: 0,
      fading: false,
      fade_timer: VfxBlockShatterTask.DEBRIS_FADE_DURATION
    } );
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.root.position.copy( pos );
  }

  public stop( behavior?: StopBehavior ): void
  {
    this.is_active = false;
    this.disposeAll();
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( this.decorative_disposed && this.promoted.length === 0 )
    {
      this.countOut();
      return true;
    }

    const age = now - this.start_time;
    const dt = Math.max( 0, now - ( this.last_now ?? this.start_time ) );
    this.last_now = now;

    if ( !this.decorative_disposed )
    {
      const progress = age / this.duration;

      if ( progress >= 1.0 )
      {
        this.disposeDecorative();
      }
      else
      {
        this.material.opacity = Math.max( 0, 1.0 - progress );

        const temp_quat = new THREE.Quaternion();

        const scale_val = this.root.scale.x > 0 ? this.root.scale.x : 1.0;
        const inv_scale = 1.0 / scale_val;
        const local_floor_y = ( this.floor_y - this.root.position.y ) * inv_scale;
        const local_gravity_offset = 0.5 * VfxBlockShatterTask.GRAVITY * age * age * inv_scale;
        const local_age_inv_scale = age * inv_scale;

        for ( const frag of this.fragments )
        {
          const stop_time = 1.0 / frag.friction;
          const move_time = Math.min( age, stop_time );
          const damping = 1.0 - 0.5 * frag.friction * move_time;

          const dx = frag.vx * move_time * damping * inv_scale;
          const dz = frag.vz * move_time * damping * inv_scale;

          const local_unclamped_y = frag.initial_pos.y + frag.vy * local_age_inv_scale - local_gravity_offset;
          const local_y = Math.max( local_floor_y, local_unclamped_y );

          frag.mesh.position.set(
            frag.initial_pos.x + dx,
            local_y,
            frag.initial_pos.z + dz
          );

          const delta_angle = frag.rot_speed * move_time * damping;
          temp_quat.setFromAxisAngle( frag.rot_axis, delta_angle );
          frag.mesh.quaternion.copy( frag.initial_quat ).multiply( temp_quat );
        }
      }
    }

    this.updatePromotedFragments( dt );

    const finished = this.decorative_disposed && this.promoted.length === 0;

    if ( finished )
      this.countOut();

    return finished;
  }

  private updatePromotedFragments( dt: number ): void
  {
    for ( let i = this.promoted.length - 1; i >= 0; i-- )
    {
      const p = this.promoted[i];
      p.age += dt;

      const t = p.body.translation();
      const r = p.body.rotation();
      p.mesh.position.set( t.x, t.y, t.z );
      p.mesh.quaternion.set( r.x, r.y, r.z, r.w );

      if ( p.fading )
      {
        p.fade_timer -= dt;
        p.material.opacity = Math.max( 0, p.fade_timer / VfxBlockShatterTask.DEBRIS_FADE_DURATION );
        if ( p.fade_timer <= 0 )
        {
          this.disposePromoted( p );
          this.promoted.splice( i, 1 );
        }
        continue;
      }

      const lv = p.body.linvel();
      const is_resting = p.body.isSleeping() ||
        ( lv.x * lv.x + lv.y * lv.y + lv.z * lv.z ) < VfxBlockShatterTask.DEBRIS_REST_LINVEL_SQ_THRESHOLD;
      const below_floor = t.y < this.floor_y - VfxBlockShatterTask.DEBRIS_FALL_MARGIN;

      if (
        below_floor ||
        p.age > VfxBlockShatterTask.DEBRIS_MAX_LIFETIME ||
        ( is_resting && p.age > VfxBlockShatterTask.DEBRIS_MIN_SETTLE_TIME )
      )
      {
        p.fading = true;
        p.material.transparent = true;
        p.material.depthWrite = false;
        p.material.needsUpdate = true;
      }
    }
  }

  private disposeDecorative(): void
  {
    if ( this.decorative_disposed ) return;
    this.decorative_disposed = true;

    if ( this.root.parent )
      SceneManager.world.remove( this.root );

    this.material.dispose();

    this.root.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
        VfxBlockShatterTask.disposeOwned( child.geometry );
    } );

    this.fragments = [];
  }

  private disposePromoted( p: PromotedFragment ): void
  {
    try { PhysicsWorld.getWorld().removeRigidBody( p.body ); } catch {}

    if ( p.mesh.parent )
      SceneManager.world.remove( p.mesh );

    p.material.dispose();
    VfxBlockShatterTask.disposeOwned( p.mesh.geometry );
  }

  private static disposeOwned( geometry: THREE.BufferGeometry | undefined ): void
  {
    if ( geometry && VfxBlockShatterTask.owned_geometries.has( geometry ) )
      geometry.dispose();
  }

  private disposeAll(): void
  {
    this.disposeDecorative();

    while ( this.promoted.length > 0 )
      this.disposePromoted( this.promoted.pop()! );

    this.countOut();
  }

  private countOut(): void
  {
    if ( this.counted_out )
      return;

    this.counted_out = true;
    VfxBlockShatterTask.live.delete( this );
    VfxBlockShatterTask.live_tasks = Math.max( 0, VfxBlockShatterTask.live_tasks - 1 );
  }

  private counted_out = false;
}
